import React from 'react'
import { Box, Flex, Link, Table, Text, useAppTheme } from '@workwhile/ui'
import styles from './JurisdictionsTable.module.css'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowRight } from 'lucide-react'
import { useJurisdictionsQuery } from 'queries/wage/useJurisdictionsQuery'
import { useJurisdictionsContextValue } from '../context/JurisdictionsProvider'
import * as ROUTES from 'constants/routes'

type Jurisdiction = NonNullable<
  ReturnType<typeof useJurisdictionsQuery>['data']
>[number]

const LevelBadge = ({ level }: { level: string }) => {
  const { colors } = useAppTheme()
  const levelColors: Record<string, string> = {
    federal: colors.info,
    state: colors.success,
    county: colors.warning,
    place: colors.primary,
  }
  return (
    <span
      className={styles.levelBadge}
      style={{ backgroundColor: levelColors[level] ?? colors.neutrals[400] }}
    >
      {level}
    </span>
  )
}

const columns: ColumnDef<Jurisdiction>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
  },
  {
    header: 'Name',
    accessorKey: 'name',
  },
  {
    header: 'Level',
    accessorKey: 'level',
    cell: ({ row }) => <LevelBadge level={row.original.level} />,
  },
  {
    header: 'State',
    accessorKey: 'stateCode',
    cell: ({ row }) => row.original.stateCode ?? '—',
  },
  {
    header: 'County FIPS',
    accessorKey: 'countyFips',
    cell: ({ row }) => row.original.countyFips ?? '—',
  },
  {
    header: 'Place GEOID',
    accessorKey: 'placeGeoid',
    cell: ({ row }) => row.original.placeGeoid ?? '—',
  },
  {
    header: 'Detail',
    cell: ({ row }) => {
      const { colors } = useAppTheme()
      return (
        <Link
          to={ROUTES.WAGE_JURISDICTION_DETAIL.replace(
            ':jurisdictionId',
            String(row.original.id)
          )}
        >
          View <ArrowRight size={14} color={colors.primary} />
        </Link>
      )
    },
  },
]

export const JurisdictionsTable = () => {
  const {
    state: { filteredData, pagination },
    actions: { setPagination },
  } = useJurisdictionsContextValue()

  if (filteredData.length === 0) {
    return (
      <Flex
        mb={2}
        flexDirection="column"
        alignItems="center"
        color="lightText"
        height={258}
        justifyContent="center"
        maxWidth={['100%', '100%', '50%', '50%']}
        margin="auto"
      >
        <Text>No jurisdictions matched your search.</Text>
      </Flex>
    )
  }

  return (
    <Box>
      <Table
        columns={columns}
        data={filteredData}
        enableSorting
        clientPagination={{ state: pagination, setState: setPagination }}
      />
    </Box>
  )
}
